import { AppThunkAction } from ".";
import { Reducer, Action } from "redux";
import {
  PohodaClient,
  OrderReceipt,
  IssuedOrder,
  ReceivedOrderProduct,
} from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import { getApiErrorMessage, type ApiError } from "../utils/apiError";

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface SendReceiptState {
  isSending: boolean;
  isSentSuccessfully: boolean;
  newReceiptNumber: string;
  newReceiptCompanyName: string;
  error: string;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

interface SendReceiptAction {
  type: "SEND_RECEIPT";
}

export interface ReceiveReceiptResponseAction {
  type: "RECEIVE_RECEIPT_RESPONSE";
  newReceiptNumber: string;
  newReceiptCompanyName: string;
}

interface CancelSendReceiptAction {
  type: "CANCEL_SEND_RECEIPT";
  reason: string;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | SendReceiptAction
  | ReceiveReceiptResponseAction
  | CancelSendReceiptAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

// Initialize the API client
const pohodaClient = new PohodaClient(new ApiConfig());

export const actionCreators = {
  sendReceipt:
    (
      IssuedOrder: IssuedOrder,
      ReceivedItems: ReceivedOrderProduct[]
    ): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      const missingGoodsWillBeDeliveredLater =
        getState().orderIntake?.missingGoodsWillBeDeliveredLater ?? false;

      // Convert local data to backend interface
      const backendOrderReceipt: OrderReceipt = {
        IssuedOrder,
        ReceivedItems,
        MissingGoodsWillBeDeliveredLater: missingGoodsWillBeDeliveredLater,
      };

      pohodaClient
        .createOrderReceipt(backendOrderReceipt)
        .then((data) => {
          dispatch({
            type: "RECEIVE_RECEIPT_RESPONSE",
            newReceiptNumber: data.orderReceiptNumber,
            newReceiptCompanyName: data.orderReceiptCompanyName,
          });
        })
        .catch((error: ApiError) =>
          dispatch({
            type: "CANCEL_SEND_RECEIPT",
            reason: getApiErrorMessage(error),
          })
        );

      dispatch({ type: "SEND_RECEIPT" });
    },
  //updateProductCode: (event: ChangeEvent<HTMLInputElement>) => ({ type: 'UPDATE_PRODUCT_CODE', code: event.target.value } as UpdateProductCodeAction),
  //selectBatch: (batch: ProductBatch) => ({ type: 'SELECT_BATCH', batch: batch } as SelectBatchAction),
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

const unloadedState: SendReceiptState = {
  isSending: false,
  isSentSuccessfully: false,
  error: "",
  newReceiptNumber: "",
  newReceiptCompanyName: "",
};

export const reducer: Reducer<SendReceiptState> = (
  state: SendReceiptState | undefined,
  incomingAction: Action
): SendReceiptState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case "SEND_RECEIPT":
      return {
        ...state,
        error: "",
        isSending: true,
        isSentSuccessfully: false,
        newReceiptNumber: "",
        newReceiptCompanyName: "",
      };
    case "RECEIVE_RECEIPT_RESPONSE":
      return {
        ...state,
        error: "",
        isSending: false,
        isSentSuccessfully: true,
        newReceiptNumber: action.newReceiptNumber,
        newReceiptCompanyName: action.newReceiptCompanyName,
      };
    case "CANCEL_SEND_RECEIPT":
      return {
        ...state,
        isSending: false,
        isSentSuccessfully: false,
        error: action.reason,
      };
    default:
      return state;
  }
};
